import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as React from 'react';

import { cn } from '../lib/utils';

export type DialogRootProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Root>;

const DialogRoot = DialogPrimitive.Root;

export type DialogTriggerProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Trigger>;

const DialogTrigger = DialogPrimitive.Trigger;

export type DialogPortalProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Portal>;

const DialogPortal = DialogPrimitive.Portal;

export type DialogCloseProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>;

const DialogClose = DialogPrimitive.Close;

export type DialogOverlayProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay> & { ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Overlay> | null> };

function DialogOverlay({ ref, className, ...props }: DialogOverlayProps) {
    return (
        <DialogPrimitive.Overlay
            ref={ref}
            className={cn(
                'fixed inset-0 z-50 bg-background-popover/75 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
                className,
            )}
            {...props}
        />
    );
}
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

export type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Content> | null>;
    autoFocus?: boolean;
};

function DialogContent({ autoFocus = false, ref, className, children, ...props }: DialogContentProps) {
    return (
        <DialogPortal>
            <DialogOverlay />
            <DialogPrimitive.Content
                onOpenAutoFocus={e => !autoFocus ? e.preventDefault() : undefined}
                ref={ref}
                className={cn(
                    'fixed left-[50%] top-[50%] z-50 flex max-h-[75%] w-full max-w-lg translate-x-[-50%] translate-y-[-50%] flex-col border bg-background-primary shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-3xl',
                    className,
                )}
                {...props}
            >
                {children}
            </DialogPrimitive.Content>
        </DialogPortal>
    );
}
DialogContent.displayName = DialogPrimitive.Content.displayName;

export type DialogHeaderProps = React.HTMLAttributes<HTMLDivElement>;

function DialogHeader({ className, ...props }: DialogHeaderProps) {
    return (
        <div
            className={cn(
                'flex flex-col space-y-1.5 rounded-t-3xl border-b p-6 text-center',
                className,
            )}
            {...props}
        />
    );
}
DialogHeader.displayName = 'DialogHeader';

export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;

function DialogFooter({ className, ...props }: DialogFooterProps) {
    return (
        <div
            className={cn(
                'flex gap-3 rounded-b-3xl border-t p-6 text-center',
                className,
            )}
            {...props}
        />
    );
}
DialogFooter.displayName = 'DialogFooter';

export type DialogBodyProps = React.HTMLAttributes<HTMLDivElement>;

function DialogBody({ className, ...props }: DialogBodyProps) {
    return <div className={cn('flex flex-col gap-2 px-6', className)} {...props} />;
}
DialogBody.displayName = 'DialogBody';

export type DialogTitleProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title> & { ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Title> | null> };

function DialogTitle({ ref, className, ...props }: DialogTitleProps) {
    return (
        <DialogPrimitive.Title
            ref={ref}
            className={cn(
                'text-lg font-medium leading-none tracking-tight',
                className,
            )}
            {...props}
        />
    );
}
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export type DialogDescriptionProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description> & { ref?: React.RefObject<React.ComponentRef<typeof DialogPrimitive.Description> | null> };

function DialogDescription({ ref, className, ...props }: DialogDescriptionProps) {
    return (
        <DialogPrimitive.Description
            ref={ref}
            className={cn('text-sm text-muted-foreground', className)}
            {...props}
        />
    );
}
DialogDescription.displayName = DialogPrimitive.Description.displayName;

/**
 * Dialog Compound Component
 */
const Dialog = Object.assign(DialogRoot, {
    Body: DialogBody,
    Close: DialogClose,
    Content: DialogContent,
    Description: DialogDescription,
    Header: DialogHeader,
    Footer: DialogFooter,
    Title: DialogTitle,
    Trigger: DialogTrigger,
    Overlay: DialogOverlay,
    Portal: DialogPortal,
});

export {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
};
